import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserUtility } from '@utility/browser-utility';
import { CommonUtility } from '@utility/common';
import {
  CommonConstant,
  ErrorMessageConstant,
} from '@utility/constants/common';
import { statusCodes } from '@utility/constants/api';
import ItemService from '@utility/services/item';
import CatalogService from '@utility/services/catalog';
import ItemBundlesService from '@utility/services/bundle';
import { useDebouncedMemo } from './debounceAsync';

/*
 * Items as the SCM screens (suppliers, assign items, restock) and the item
 * pickers read them. On react-query; each hook keeps the shape its callers
 * destructure.
 */

/* A shared empty list, so callers that memoize on it see one reference. */
// biome-ignore lint/suspicious/noExplicitAny: legacy screens type the rows themselves.
const NO_ROWS: any[] = [];

export const itemKeys = {
  all: ['items'] as const,
  unassignedCount: () => [...itemKeys.all, 'unassigned-count'] as const,
  unassigned: (params: unknown) =>
    [...itemKeys.all, 'unassigned', params] as const,
  unassignedTotals: (params: unknown) =>
    [...itemKeys.all, 'unassigned-totals', params] as const,
  unassignedChildren: (id: string, params: unknown) =>
    [...itemKeys.all, 'unassigned-children', id, params] as const,
  bundleByItem: (itemId: string) =>
    [...itemKeys.all, 'bundle-by-item', itemId] as const,
  supplierItems: (supplierId: string, params: unknown, cache: boolean) =>
    [...itemKeys.all, 'supplier', supplierId, { params, cache }] as const,
  dropdown: (params: unknown) => [...itemKeys.all, 'dropdown', params] as const,
  restock: (params: unknown) => [...itemKeys.all, 'restock', params] as const,
};

type PageFilter = {
  limit: number;
  page: number;
  search: string;
  [key: string]: unknown;
};

const firstPage = (): PageFilter => ({
  limit: CommonConstant.defaultPageSize,
  page: 1,
  search: '',
});

const apiError = (
  error: unknown,
  fallback = ErrorMessageConstant.defaultAPIError,
) =>
  (error as { message?: string; error?: string } | null)?.message ||
  (error as { error?: string } | null)?.error ||
  (error ? fallback : '');

/** Page, size and search, with the setters the legacy tables call. */
function usePageFilter(initial: () => PageFilter) {
  const [filter, setFilter] = useState<PageFilter>(initial);

  const pageChanged = useCallback(
    (page: number) => setFilter(previous => ({ ...previous, page })),
    [],
  );
  const filterChanged = useCallback(
    (key: string, value: unknown) =>
      setFilter(previous => ({ ...previous, page: 1, [key]: value })),
    [],
  );
  const setSearch = useCallback(
    (search: string) => filterChanged('search', search),
    [filterChanged],
  );

  return { filter, setFilter, pageChanged, filterChanged, setSearch };
}

/* A copy of the rows the screen may edit in place; it follows each new answer.
   Loose on purpose: the legacy screens read these rows under their own types. */
// biome-ignore lint/suspicious/noExplicitAny: see above.
function useEditableRows(rows: any[] | undefined) {
  // biome-ignore lint/suspicious/noExplicitAny: see above.
  const [data, setData] = useState<any[]>([]);
  useEffect(() => {
    if (rows) setData(rows);
  }, [rows]);
  return [data, setData] as const;
}

/** How many items no supplier claims yet. */
export const unassignedItemCount = () => {
  const query = useQuery({
    queryKey: itemKeys.unassignedCount(),
    queryFn: async () => {
      // @ts-expect-error - TS2554: Expected 1 arguments, but got 0.
      const result = await ItemService.unassignedCount();
      return (result?.data?.total_data ?? 0) as number;
    },
  });

  return {
    count: query.isError ? 0 : (query.data ?? 0),
    loading: query.isFetching,
    doRefresh: () => void query.refetch(),
  };
};

/** The bundle a bundle item belongs to; nothing for a plain item. */
export const useBundleByItemHook = (isBundle, itemId) => {
  const query = useQuery({
    queryKey: itemKeys.bundleByItem(itemId ?? ''),
    queryFn: async () => {
      const result = await ItemBundlesService.byItem(itemId);
      return result?.data?.[0] ?? null;
    },
    enabled: !!isBundle && !!itemId,
  });

  return {
    data: query.isError ? null : (query.data ?? null),
    loading: query.isFetching,
    doRefresh: () => void query.refetch(),
  };
};

/** Items without a supplier, a page at a time, with their total counted apart. */
export const unassignedItemsHook = stateFilter => {
  const { filter, setFilter, pageChanged, filterChanged, setSearch } =
    usePageFilter(firstPage);

  /* A new state filter starts over from the first page. */
  const stateKey = JSON.stringify(stateFilter ?? {});
  useEffect(() => {
    setFilter(previous => ({ ...previous, page: 1 }));
  }, [stateKey]);

  const params = useDebouncedMemo(
    () => ({ ...filter, ...stateFilter, search: filter.search.trim() }),
    [JSON.stringify(filter), stateKey],
    500,
  );

  const list = useQuery({
    queryKey: itemKeys.unassigned(params),
    queryFn: async () => {
      const result = await ItemService.unassignedItems(params);
      return (result?.data ?? []).map(item =>
        item._id ? item : { ...item, _id: item.item?._id || Math.random() },
      );
    },
    enabled: !!params,
  });

  const totals = useQuery({
    queryKey: itemKeys.unassignedTotals(params),
    queryFn: async () => {
      const result = await ItemService.unassignedCount(params);
      return {
        pages: (result?.data?.total_pages ?? 0) as number,
        rows: (result?.data?.total_data ?? 0) as number,
      };
    },
    enabled: !!params,
  });

  const [data, setData] = useEditableRows(list.data);

  return {
    loading: !params || list.isFetching,
    data,
    error: apiError(list.error ?? totals.error),
    totalPages: totals.data?.pages ?? 0,
    totalData: totals.data?.rows ?? 0,
    setData,
    pageChanged,
    filterChanged,
    filter,
    refresh: () => {
      void list.refetch();
      void totals.refetch();
    },
    search: filter.search,
    setSearch,
    count: totals.data?.rows ?? 0,
  };
};

/** A parent item's children that no supplier claims yet. */
export const unassignedChildItemsHook = (id, searchQuery, stateFilter) => {
  const params = useDebouncedMemo(
    () => ({ search: searchQuery, ...stateFilter }),
    [searchQuery, JSON.stringify(stateFilter ?? {})],
    500,
  );

  const query = useQuery({
    queryKey: itemKeys.unassignedChildren(id ?? '', params),
    queryFn: () => ItemService.unassignedChildren(id, params),
    enabled: !!id && !!params,
  });

  return {
    loading: query.isFetching,
    data: query.data ?? NO_ROWS,
    error: apiError(query.error),
  };
};

/** A supplier's items, most predicted first, a page at a time. */
export const supplierItemsHook = (supplierId, cache = false) => {
  const { filter, setFilter, pageChanged, filterChanged, setSearch } =
    usePageFilter(firstPage);
  const [refreshKey, setRefreshKey] = useState(0);

  const params = useDebouncedMemo(
    () => ({ ...filter, search: filter.search.trim() }),
    [JSON.stringify(filter)],
    500,
  );

  const query = useQuery({
    queryKey: itemKeys.supplierItems(supplierId ?? '', params, cache),
    queryFn: async () => {
      const result = await ItemService.supplierItems(supplierId, params, cache);
      return {
        rows: [...(result?.data ?? [])].sort(
          (a, b) => (b.predictedQty || 0) - (a.predictedQty || 0),
        ),
        pages: result?.total_pages ?? 0,
        total: result?.total_data ?? 0,
      };
    },
    enabled: !!supplierId && !!params,
    placeholderData: keepPreviousData,
  });

  const [data, setData] = useEditableRows(query.data?.rows);

  /* A refresh asks again even when nothing in the filter moved. */
  useEffect(() => {
    if (refreshKey) void query.refetch();
  }, [refreshKey]);

  return {
    data,
    setData,
    loading: !params || query.isFetching,
    error: query.error
      ? (query.error as { error?: string }).error ||
        ErrorMessageConstant.defaultAPIError
      : '',
    totalPages: query.data?.pages ?? 0,
    filterChanged,
    pageChanged,
    filter,
    setFilter,
    totalData: query.data?.total ?? 0,
    search: filter.search,
    setSearch,
    refresh: () => setRefreshKey(key => key + 1),
  };
};

type DropdownPage = { data?: unknown[]; total_pages?: number };

/** Items to pick from, a page more each time the list is scrolled to its end. */
export const itemsDropdownHook = (
  regionId,
  search,
  optionalRegion = false,
  isInternational = false,
) => {
  const params = useDebouncedMemo(
    () => ({ regionId, search, optionalRegion, isInternational }),
    [regionId, search, optionalRegion, isInternational],
    500,
  );

  const query = useInfiniteQuery({
    queryKey: itemKeys.dropdown(params),
    initialPageParam: 1,
    queryFn: async ({ pageParam, signal }): Promise<DropdownPage> => {
      if (optionalRegion) {
        return CatalogService.getAutocomplete({ search }, signal);
      }
      return ItemService.itemDropdown(
        regionId,
        { search, page: pageParam, is_international: isInternational },
        signal,
      );
    },
    getNextPageParam: (last, pages) =>
      !optionalRegion && pages.length < (last?.total_pages || 1)
        ? pages.length + 1
        : undefined,
    enabled: !!params && (!!regionId || optionalRegion),
  });

  // biome-ignore lint/suspicious/noExplicitAny: the picker reads rows under its own type.
  const data = useMemo<any[]>(
    () => query.data?.pages.flatMap(page => page?.data ?? []) ?? NO_ROWS,
    [query.data],
  );

  const failed = query.error as { status?: unknown; message?: string } | null;
  const nextPage = useCallback(() => {
    if (query.hasNextPage && !query.isFetching) void query.fetchNextPage();
  }, [query.hasNextPage, query.isFetching, query.fetchNextPage]);

  return {
    loading: query.isFetching,
    data,
    error:
      failed && failed.status !== statusCodes.axiosStatusCode
        ? failed.message || ErrorMessageConstant.defaultAPIError
        : '',
    nextPage,
  };
};

const RESTOCK_FILTER_KEY = 'catalogRestockList';

/** The restock list of a region, each row with its sales figures merged in. */
export const hookCatalogRestockList = (stateFilter, marketplace_region_id) => {
  const { filter, setFilter, pageChanged, filterChanged, setSearch } =
    usePageFilter(() =>
      BrowserUtility.getTableFilter(RESTOCK_FILTER_KEY, {
        ...firstPage(),
        sort: { ETD: 'desc' },
      }),
    );
  const [showPredictedPO, setShowPredictedPO] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    BrowserUtility.updateTableFilter(RESTOCK_FILTER_KEY, filter);
  }, [filter]);

  /* A new region or state filter starts over from the first page. */
  const stateKey = JSON.stringify(stateFilter ?? {});
  useEffect(() => {
    setFilter(previous => ({ ...previous, page: 1, marketplace_region_id }));
  }, [stateKey, marketplace_region_id]);

  const params = useDebouncedMemo(
    () => {
      const temp = {
        ...filter,
        ...(filter.sort as object),
        ...stateFilter,
        predictPO: showPredictedPO ? '' : false,
        sort: null,
        search: filter.search.trim(),
      };
      delete temp.regionId;
      return temp;
    },
    [JSON.stringify(filter), stateKey, showPredictedPO],
    500,
  );

  const query = useQuery({
    queryKey: itemKeys.restock(params),
    queryFn: async () => {
      const result = await ItemService.catalogRestockList(params);
      const rows = CommonUtility.isValidArray(result?.catalogs)
        ? result.catalogs
        : CommonUtility.isValidArray(result)
          ? result
          : [];

      const ids = rows.map(row => row._id);
      const sales = CommonUtility.isValidArray(ids)
        ? await CatalogService.ItemSalesData({ items: ids })
        : [];

      return {
        rows: rows.map(row => {
          const figures = sales.find(entry => entry._id === row._id);
          return figures ? { ...row, ...figures } : row;
        }),
        pages: result?.total_pages || 1,
        total: result?.total_data || rows.length,
      };
    },
    enabled: !!params?.marketplace_region_id,
  });

  const [data, setData] = useEditableRows(query.data?.rows);

  useEffect(() => {
    if (refreshKey) void query.refetch();
  }, [refreshKey]);

  return {
    data,
    error: apiError(query.error),
    filter,
    filterChanged,
    loading: query.isFetching,
    pageChanged,
    search: filter.search,
    setData,
    setSearch,
    totalData: query.data?.total ?? 0,
    totalPages: query.data?.pages ?? 0,
    refresh: () => setRefreshKey(key => key + 1),
    showPredictedPO,
    setShowPredictedPO,
  };
};
